import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Form,
  RadioGroup,
  RadioGroupItem,
  Skeleton,
  toast,
} from '@qodo/design-system';
import { FEATURE_FLAGS } from 'consts/features';
import { usePATFlow } from 'hooks/usePATFlow';
import { useIsFeatureEnabledQuery } from 'network/queries/useIsFeatureEnabledQuery';
import { ROUTES } from 'routes/consts';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { ExternalLink } from 'components/ExternalLink';
import { useAzureInstallation } from '../azureInstallationContext';
import {
  CONNECTION_METHODS,
  FORM_IDS,
  OAUTH_PARAMS,
  QODO_APP_DOCUMENTATION_LINK,
  QODO_PAT_DOCUMENTATION_LINK,
  STEP_IDS,
} from '../consts';
import { PatMethodSection } from '../parts';
import { ConnectionMethod } from '../types';
import {
  clearConsentState,
  decodeErrorDescription,
  isValidAzureOrgUrl,
} from '../utils';

export const ChooseMethod = () => {
  const spawnStep = spawn('dialogs.azure-installation.steps.installation-type');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    connectionMethod,
    setConnectionMethod,
    setAzureTenantId,
    setConsentGranted,
    setConsentError,
  } = useAzureInstallation();
  const {
    setIsNextEnabled,
    setFurthestStepReached,
    setIsPending,
    setStepDisabled,
    steps,
  } = useMultiStepDialog();
  const methods = useForm();
  const { state: patState, handlers: patHandlers } = usePATFlow();
  const {
    data: isAzureAppConsentEnabled,
    isLoading,
    isFetched,
  } = useIsFeatureEnabledQuery(FEATURE_FLAGS.AZURE_APP_CONSENT);
  const isAppRegistrationEnabled = Boolean(isAzureAppConsentEnabled);

  useEffect(() => {
    const errorDescription = searchParams.get('error_description');

    if (errorDescription) {
      const decodedError = decodeErrorDescription(errorDescription);

      toast.error(decodedError, {
        duration: 10000,
      });

      // Clean up OAuth params from URL
      const cleaned = new URLSearchParams(searchParams);

      OAUTH_PARAMS.forEach((param) => cleaned.delete(param));
      setSearchParams(cleaned, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (isFetched && !connectionMethod) {
      if (!isAzureAppConsentEnabled) {
        setStepDisabled(STEP_IDS.REVIEW_MANIFEST, true);
        setStepDisabled(STEP_IDS.INSTALL_GUIDE, true);
        setConnectionMethod(CONNECTION_METHODS.PAT);
        setIsNextEnabled(patState.patValue.trim().length > 0);
      } else {
        setConnectionMethod(CONNECTION_METHODS.APP_REGISTRATION);
        setIsNextEnabled(true);
      }
    }
  }, [isFetched, connectionMethod, patState.patValue]);

  const onSelect = (method: ConnectionMethod) => {
    setConnectionMethod(method);
    setAzureTenantId(undefined);
    clearConsentState();
    setConsentGranted(false);
    setConsentError(undefined);

    if (method === CONNECTION_METHODS.APP_REGISTRATION) {
      setIsNextEnabled(true);
      setStepDisabled(STEP_IDS.REVIEW_MANIFEST, false);
      setStepDisabled(STEP_IDS.INSTALL_GUIDE, false);
    } else {
      setIsNextEnabled(patState.patValue.trim().length > 0);
      setStepDisabled(STEP_IDS.REVIEW_MANIFEST, true);
      setStepDisabled(STEP_IDS.INSTALL_GUIDE, true);
    }
  };

  const onPatInputChange = (value: string) => {
    patHandlers.onPatChange(value);
    setIsNextEnabled(value.trim().length > 0);
  };

  const onOrgUrlInputChange = (value: string) => {
    patHandlers.onOrgUrlChange(value);
    setIsNextEnabled(isValidAzureOrgUrl(value));
  };

  const onSubmit = async () => {
    if (!connectionMethod) return;

    if (connectionMethod === CONNECTION_METHODS.APP_REGISTRATION) {
      const reviewManifestIndex = steps.findIndex(
        (s) => s.id === STEP_IDS.REVIEW_MANIFEST,
      );

      setFurthestStepReached(reviewManifestIndex);
      navigate(ROUTES.AZURE_INSTALLATION_STEP_1);

      return;
    }

    setIsPending(true);

    try {
      const result = await patHandlers.onSubmit();

      if (result.status === 'connected') {
        const connectReposIndex = steps.findIndex(
          (s) => s.id === STEP_IDS.CONNECT_REPOS,
        );

        setStepDisabled(STEP_IDS.CHECK_CONNECTION, true);
        setFurthestStepReached(connectReposIndex);
        navigate(ROUTES.AZURE_INSTALLATION_STEP_3);
      }
    } catch {
      // Error is already set in the hook
    } finally {
      setIsPending(false);
    }
  };

  const renderBadge = () => {
    if (isAppRegistrationEnabled)
      return <Badge>{spawnStep('recommended-label')}</Badge>;

    return <Badge>{spawnStep('coming-soon-label')}</Badge>;
  };

  const renderChooseMethodContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-18.5 w-full" />
          <Skeleton className="h-18.5 w-full" />
        </div>
      );
    }

    return (
      <RadioGroup
        name="connection-method"
        value={connectionMethod ?? undefined}
        onValueChange={(value) => onSelect(value as ConnectionMethod)}
        direction="vertical"
        className="gap-4"
      >
        <RadioGroupItem
          value={CONNECTION_METHODS.APP_REGISTRATION}
          name="connection-method"
          disabled={!isAppRegistrationEnabled}
          label={
            <span className="flex items-center gap-3 font-normal text-sm">
              {spawnStep('app-registration-label')}
              {renderBadge()}
            </span>
          }
          description={
            <div>
              <span>{spawnStep('app-registration-description')}</span>{' '}
              <div className="inline">
                <ExternalLink
                  url={QODO_APP_DOCUMENTATION_LINK}
                  className="self-start text-xs text-text-general-neutral-mid"
                  iconClassName="stroke-text-general-neutral-mid"
                >
                  {spawnStep('learn-more')}
                </ExternalLink>
              </div>
            </div>
          }
          wrapperClassName={cn(
            `p-3 rounded-sm bg-fill-interactive-neutral-idle border border-transparent has-[button[aria-checked='true']]:border-border-input-emphasis-active`,
            { 'pointer-events-none': !isAppRegistrationEnabled },
          )}
        />

        <RadioGroupItem
          value={CONNECTION_METHODS.PAT}
          name="connection-method"
          label={spawnStep('pat-label')}
          description={
            <div>
              <span>{spawnStep('pat-description')}</span>{' '}
              <div className="inline">
                <ExternalLink
                  url={QODO_PAT_DOCUMENTATION_LINK}
                  className="self-start text-xs text-text-general-neutral-mid"
                  iconClassName="stroke-text-general-neutral-mid"
                >
                  {spawnStep('learn-more')}
                </ExternalLink>
              </div>
            </div>
          }
          wrapperClassName="p-3 rounded-sm bg-fill-interactive-neutral-idle border border-transparent has-[button[aria-checked='true']]:border-border-input-emphasis-active"
        />
      </RadioGroup>
    );
  };

  return (
    <Step title={spawnStep('title')} description={spawnStep('description')}>
      <Form
        {...({ id: FORM_IDS.CHOOSE_METHOD } as Record<string, string>)}
        methods={methods}
        onSubmit={onSubmit}
      >
        <div className="flex flex-col gap-4 pt-4">
          {renderChooseMethodContent()}
          <PatMethodSection
            isSelected={connectionMethod === CONNECTION_METHODS.PAT}
            phase={patState.phase}
            setPhase={patHandlers.setPhase}
            patValue={patState.patValue}
            orgUrl={patState.orgUrl}
            error={patState.error}
            onPatInputChange={onPatInputChange}
            onOrgUrlInputChange={onOrgUrlInputChange}
          />
        </div>
      </Form>
    </Step>
  );
};
